import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import keycloak from '@/auth/keycloakConfig';

const axiosClient = axios.create({
  baseURL: '/api/v1',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add JWT token
axiosClient.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    if (keycloak.token) {
      // Refresh token if needed
      try {
        await keycloak.updateToken(5);
      } catch (error) {
        console.error('Failed to refresh token', error);
        keycloak.login();
      }

      config.headers.Authorization = `Bearer ${keycloak.token}`;
    }
    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  },
);

// Response interceptor to handle errors (with retry for network failures)
axiosClient.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error: AxiosError) => {
    const config = error.config as InternalAxiosRequestConfig & { _retryCount?: number };
    // Retry on network errors (backend port-forward dropped) — up to 2 retries with 1s delay
    if (!error.response && config && (config._retryCount ?? 0) < 2) {
      config._retryCount = (config._retryCount ?? 0) + 1;
      await new Promise((r) => setTimeout(r, 1000));
      return axiosClient.request(config);
    }
    if (error.response?.status === 401) {
      try {
        await keycloak.updateToken(30);
        if (keycloak.token && error.config) {
          const retryConfig = error.config as InternalAxiosRequestConfig;
          retryConfig.headers.Authorization = `Bearer ${keycloak.token}`;
          return axiosClient.request(retryConfig);
        }
      } catch (refreshError) {
        console.error('Token refresh failed', refreshError);
        keycloak.login();
      }
    }
    if (error.response?.status === 403) {
      console.error('Access denied:', (error.response?.data as Record<string, unknown>)?.message || 'Forbidden');
    }
    return Promise.reject(error);
  },
);

export default axiosClient;
